import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    useGetQueueProjectHopsQuery,
    useGetQueueProjectScopeMembershipQuery,
    useGetRelatedQueueProjectsQuery
} from "generated/graphql";
import React, { useMemo } from "react";
import { Loading } from "types/loadingType";
import {
    convertHasuraDataToRelatedQueueProjects,
    mergeRelatedProjectsAndHops,
    RelatedQueueProjectsTable
} from "types/queueProjectRelatednessType";
import { RegionType } from "types/regionType";
import {
    MISO_2022_REALISTIC_SCOPE_TYPE,
    MISO_PESSIMISTIC_SCOPE_TYPE
} from "types/scope";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "types/scopeViewType";
import DfaxRelatednessSection, {
    CurrentScopeMembership
} from "./DfaxRelatednessSection";

const SCOPE_MEMBERSHIP_REGIONS: ReadonlySet<RegionType> = new Set([
    RegionType.MISO,
    RegionType.WECC
]);

const getCurrentScopeMembership = (
    region: RegionType,
    scope: string | undefined
): CurrentScopeMembership => {
    if (!SCOPE_MEMBERSHIP_REGIONS.has(region)) {
        return "untrackedRegion";
    }
    // The pipeline exports no membership roster for the model-based scope.
    // An empty response does not mean its projects are excluded.
    if (region === RegionType.MISO && scope === MISO_PESSIMISTIC_SCOPE_TYPE) {
        return "untrackedScope";
    }
    return "tracked";
};

/**
 * Sidebar panel showing queue projects related to the study bus.
 * Owns the relatedness/hops/membership queries; the constraints table in
 * BusResultsSection reuses the same relatedness query for its expandable
 * rows, and Apollo dedupes the two into a single network request.
 */
const RelatedProjectsPanel: React.FC = () => {
    const { bus, selectedScope, selectedGeneratorType, powerAmounts } =
        useBusPageConfiguration();

    const scopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
        bus.region,
        selectedGeneratorType
    );

    const isMisoBus = bus.region === RegionType.MISO;
    // Pin scope to miso_2022_realistic so the "related queue projects"
    // panel is a stable POI-level view that doesn't shift when the user
    // switches between scopes. scope_view still tracks the user's current
    // generator type so the underlying constraint set matches what they're
    // looking at on the left side.
    const relatednessQuery = useGetRelatedQueueProjectsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope: MISO_2022_REALISTIC_SCOPE_TYPE,
            scope_view: scopeView,
            region: bus.region
        },
        skip: !isMisoBus
    });
    // Network-proximity (hops) for the study bus, keyed on the
    // scope_view/region grain so it lines up per project with the relatedness
    // query (where present). There is no `scope` column on the hops table.
    // Runs for every region: on MISO it supplements the impact scores, and on
    // regions without relatedness data (e.g. WECC) it powers a hops-only
    // related-projects table.
    const hopsQuery = useGetQueueProjectHopsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope_view: scopeView,
            region: bus.region
        }
    });
    const maybeRelatedTable: Loading<RelatedQueueProjectsTable> =
        useMemo(() => {
            // Impact (DFAX relatedness) is MISO-only. On MISO we block on it;
            // on other regions (e.g. WECC) there is no impact data and the
            // table is built from hops alone.
            if (
                isMisoBus &&
                relatednessQuery.error === undefined &&
                (relatednessQuery.loading ||
                    relatednessQuery.data === undefined)
            ) {
                return "loading";
            }
            // Hops is supplementary on MISO but the sole metric elsewhere.
            // Wait while it's loading, but if the query errored (e.g. the
            // table isn't deployed in this environment) fall back to whatever
            // impact data we have rather than blocking the panel.
            if (hopsQuery.loading) {
                return "loading";
            }
            const impactProjects =
                isMisoBus &&
                relatednessQuery.data !== undefined &&
                relatednessQuery.error === undefined
                    ? convertHasuraDataToRelatedQueueProjects(
                          relatednessQuery.data,
                          powerAmounts
                      )
                    : [];
            const hopsData =
                hopsQuery.error !== undefined ? undefined : hopsQuery.data;
            return mergeRelatedProjectsAndHops(impactProjects, hopsData);
        }, [
            isMisoBus,
            relatednessQuery.loading,
            relatednessQuery.data,
            relatednessQuery.error,
            hopsQuery.loading,
            hopsQuery.data,
            hopsQuery.error,
            powerAmounts.energySize,
            powerAmounts.capacitySize,
            powerAmounts.chargingSize
        ]);

    // Scope-membership lookup for the project detail drawer. Runs in
    // parallel with the relatedness query and is cheap (~475 project
    // numbers per scope per region). The result is the same regardless
    // of which scope_view we pick within a scope, so we reuse the
    // generator-derived scope_view we already have.
    const isCurrentScopeMisoRealistic =
        selectedScope?.scope === MISO_2022_REALISTIC_SCOPE_TYPE;
    const currentScopeMembership = getCurrentScopeMembership(
        bus.region,
        selectedScope?.scope
    );
    const membershipQuery = useGetQueueProjectScopeMembershipQuery({
        variables: {
            region: bus.region,
            current_scope: selectedScope?.scope ?? "",
            realistic_scope: MISO_2022_REALISTIC_SCOPE_TYPE,
            scope_view: scopeView,
            include_miso_realistic: isMisoBus
        },
        skip:
            currentScopeMembership === "untrackedRegion" ||
            selectedScope === undefined
    });
    const inCurrentScopeProjectNumbers: ReadonlySet<string> | undefined =
        useMemo(() => {
            if (
                currentScopeMembership !== "tracked" ||
                membershipQuery.loading ||
                membershipQuery.error !== undefined ||
                membershipQuery.data === undefined
            ) {
                return undefined;
            }
            return new Set(
                membershipQuery.data.in_current_scope.map(
                    (row) => row.project_number
                )
            );
        }, [
            currentScopeMembership,
            membershipQuery.loading,
            membershipQuery.error,
            membershipQuery.data
        ]);
    const inMisoRealisticProjectNumbers: ReadonlySet<string> | undefined =
        useMemo(() => {
            if (
                membershipQuery.data === undefined ||
                membershipQuery.data.in_miso_realistic === undefined
            ) {
                return undefined;
            }
            return new Set(
                membershipQuery.data.in_miso_realistic.map(
                    (row) => row.project_number
                )
            );
        }, [membershipQuery.data]);

    if (!selectedScope) {
        return null;
    }

    // Surface the panel data-driven. On MISO the panel always belongs on the
    // page, so we keep it mounted even with zero rows — DfaxRelatednessSection
    // renders its empty state rather than the whole box silently vanishing
    // (which reads as a bug). On other regions we only show it once hops rows
    // actually exist, so a region with no hops data doesn't flash an
    // empty/loading panel.
    const showRelatednessPanel =
        maybeRelatedTable === "loading"
            ? isMisoBus
            : isMisoBus || maybeRelatedTable.rows.length > 0;

    if (!showRelatednessPanel) {
        return null;
    }

    return (
        <DfaxRelatednessSection
            table={maybeRelatedTable}
            currentScopeDisplayName={selectedScope.display_name}
            isMisoBus={isMisoBus}
            inCurrentScopeProjectNumbers={inCurrentScopeProjectNumbers}
            inMisoRealisticProjectNumbers={inMisoRealisticProjectNumbers}
            isCurrentScopeMisoRealistic={isCurrentScopeMisoRealistic}
            currentScopeMembership={currentScopeMembership}
            isScopeMembershipLoading={
                currentScopeMembership === "tracked" && membershipQuery.loading
            }
        />
    );
};

export default RelatedProjectsPanel;
